/**
 * Client-side image compression for uploads.
 *
 * Phone cameras produce 3–8MB photos; Vercel serverless rejects request bodies
 * over ~4.5MB, and Zimbabwean mobile upstream makes large uploads painfully slow.
 * Resizing to max 1600px JPEG ~0.82 quality turns a 5MB photo into ~250–400KB
 * with no practical quality loss for KYC review or listing photos.
 *
 * Falls back to the original file if the browser can't decode it (e.g. HEIC on
 * some Android browsers) — the server still validates magic bytes either way.
 */
export async function compressImage(file: File, maxDim = 1600, quality = 0.82): Promise<File> {
  // Skip tiny files — already small enough
  if (file.size < 400 * 1024) return file

  try {
    const bitmap = await createImageBitmap(file)
    const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height))
    const w = Math.round(bitmap.width * scale)
    const h = Math.round(bitmap.height * scale)

    const canvas = document.createElement("canvas")
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext("2d")
    if (!ctx) return file
    ctx.drawImage(bitmap, 0, 0, w, h)
    bitmap.close()

    const blob: Blob | null = await new Promise(resolve =>
      canvas.toBlob(resolve, "image/jpeg", quality)
    )
    if (!blob || blob.size === 0) return file

    // Only use the compressed version if it's actually smaller
    if (blob.size >= file.size) return file

    const newName = file.name.replace(/\.[^.]+$/, "") + ".jpg"
    return new File([blob], newName, { type: "image/jpeg" })
  } catch {
    return file
  }
}
